import getFileInfo from '@/utils/getFileInfo';

import type { BoardContentDetail } from '@/types/boardContent';
import type FileInfo from '@/types/fileInfo';
import type { FileInfoType } from '@/types/fileInfoType';

const DEFAULT_THUMBNAIL_WIDTH = 1200;
const DEFAULT_THUMBNAIL_HEIGHT = 675;

export type NewsThumbnail = {
  src: string;
  width: number;
  height: number;
  alreadyInContent: boolean;
};

export function resolveNewsVideo(
  fileInfo: FileInfoType | undefined,
  articleId: string | number,
) {
  const file = getFileInfo({
    file_info: fileInfo,
    idx: articleId,
    fileCategory: 'bc_list_video',
    isFile: true,
  });

  return file?.file_storage_path || file?.file_html_full_path || null;
}

export function resolveNewsThumbnail(
  data: Pick<BoardContentDetail, 'bc' | 'file_info'>,
): NewsThumbnail | null {
  const file = getFileInfo({
    file_info: data.file_info,
    idx: data.bc.idx,
    fileCategory: 'bc_list_img',
    isFile: true,
  });

  if (!file) {
    return null;
  }

  const src = file.file_storage_path || file.file_html_full_path;
  const width = toPositiveNumber(file.file_image_width);
  const height = toPositiveNumber(file.file_image_height);

  return {
    src,
    width: width ?? DEFAULT_THUMBNAIL_WIDTH,
    height: height ?? DEFAULT_THUMBNAIL_HEIGHT,
    alreadyInContent: isLeadingContentImage(data.bc.content, src),
  };
}

export function compactNewsArticleFiles(
  fileInfo: FileInfoType | undefined,
  articleId: number,
): FileInfoType | undefined {
  const articleKey = String(articleId);
  const categories = ['bc_list_img', 'bc_list_video', 'bc_file'] as const;
  const compactedEntries = categories.flatMap((category) => {
    const files = fileInfo?.[category]?.[articleKey];

    return files?.length
      ? [[category, { [articleKey]: files.map(compactNewsArticleFile) }]]
      : [];
  });

  if (!compactedEntries.length) {
    return undefined;
  }

  return Object.fromEntries(compactedEntries) as FileInfoType;
}

function compactNewsArticleFile(file: FileInfo): FileInfo {
  return {
    file_idx: file.file_idx,
    file_category: file.file_category,
    file_foreign_idx: file.file_foreign_idx,
    file_storage_path: file.file_storage_path,
    file_html_full_path: file.file_html_full_path,
    file_client_name: file.file_client_name,
    file_size: file.file_size,
    file_type: file.file_type,
    file_image_width: file.file_image_width,
    file_image_height: file.file_image_height,
  } as FileInfo;
}

function toPositiveNumber(value: string | number | undefined) {
  const number = Number(value);

  return Number.isFinite(number) && number > 0 ? number : null;
}

function isLeadingContentImage(content: string | undefined, thumbnail: string) {
  const imageMatch = content?.match(/<img[^>]+src=["']([^"']+)["']/i);

  if (!imageMatch?.[1] || !content) {
    return false;
  }

  const contentBeforeImage = content
    .slice(0, imageMatch.index)
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;|\s/gi, '');

  if (contentBeforeImage) {
    return false;
  }

  return normalizeAssetKey(imageMatch[1]) === normalizeAssetKey(thumbnail);
}

function normalizeAssetKey(src: string) {
  try {
    const path = new URL(src, 'https://deeplyinc.com').pathname;
    const filename = decodeURIComponent(
      path.split('/').filter(Boolean).at(-1) ?? '',
    );

    return filename.replace(/\.[a-z0-9]+$/i, '').toLowerCase();
  } catch {
    return src
      .split(/[?#]/, 1)[0]
      .replace(/\.[a-z0-9]+$/i, '')
      .toLowerCase();
  }
}
